import { Button, Col, Row, Space } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { Loader } from "~Common/Components";
import Page from "~Common/Page";
import PermissionCheck from "~Common/PermissionCheck";
import FormControl from "~components/Form/FormControl.tsx";
import { StyledFormInputs, StyledFormSelect } from "~components/Form/StyledFormInputs.tsx";
import { UploadImageForm } from "~components/Images";
import { useFetchCompetition, useFetchLeagues } from "~entities/api";
import { ImageBaseTypes } from "~enums/imageBaseTypes";
import { ImageTypes } from "~enums/imageTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import CompetitionHistoricNames from "~Features/Competitions/CompetitionHistoricNames";
import type { ILeague } from "~Features/Leagues/types.ts";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useCompetitionService } from "~pages/Competitions/hooks/useCompetitionService.ts";
import { noLeadingOrTrailingSpacesReactHookFormAdapter } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.COMPETITION;

const RightColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-left: 16px;
`;

const Content = styled.div`
    display: flex;

    .container {
        margin: 0;
    }

    .upload {
        margin-left: auto;
    }

    @media (max-width: 960px) {
        flex-direction: column;
        gap: 20px;
        .upload {
            margin-right: auto;
            margin-left: 0;
        }
    }
`;

interface IFormInput {
  nameLocal: string;
  abbreviationLocal: string;
  nameLatin: string;
  abbreviationLatin: string;
  internationalReference: string;
  leagueId: string;
  ageGroup: string;
  externalId: string;
}

const ManageCompetition = () => {
  const { sport, organizationId, customerId, competitionId } = useParams();
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();

  const { data: competition, isLoading: isFetchLoading } = useFetchCompetition();
  const { data: leaguesData, isLoading: loadingLeagues } = useFetchLeagues({
    limit: 1000,
  });

  const { ageGroups: AGE_GROUP } = useSportConfiguration();

  const leagues = useMemo(() => leaguesData?.data || [], [leaguesData?.data]);

  const { createCompetition, isCreatePending, updateCompetition, isUpdatePending } = useCompetitionService();

  const isUpsertPending = isCreatePending || isUpdatePending;
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  useEffect(() => {
    reset({
      nameLocal: competition?.nameLocal,
      nameLatin: competition?.nameLatin,
      abbreviationLocal: competition?.abbreviationLocal,
      abbreviationLatin: competition?.abbreviationLatin,
      internationalReference: competition?.internationalReference,
      leagueId: competition?.leagueId,
      ageGroup: competition?.ageGroup,
      externalId: competition?.externalId,
    });
  }, [competition, reset]);

  const title = useMemo(() => {
    return competitionId
      ? `${formatMessage({ id: "manage" })} ${competition?.nameLocal ? `- ${competition?.nameLocal}` : ""}`
      : `${formatMessage({ id: "addCompetition" })}`;
  }, [competitionId, formatMessage, competition]);

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    const payload = { ...data, externalId: data.externalId || null };

    if (competitionId) {
      await updateCompetition(competitionId, payload);
      return;
    }

    await createCompetition(payload);
  };

  const onImageUploadSuccess = () => {
    invalidateCache();
  };

  return (
    <Page title={title} tabTitle={title}>
      {loadingLeagues || isUpsertPending || isFetchLoading ? (
        <Loader />
      ) : (
        <Content>
          <div>
            <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
              <Row style={{ maxWidth: 960 }} gutter={16}>
                <Col span={12}>
                  <Row gutter={16}>
                    <Col span={16}>
                      <FormControl
                        labelText="name.local"
                        name="nameLocal"
                        labelTooltip={formatMessage(
                          {
                            id: "form.help.local.name.item",
                          },
                          {
                            item: formatMessage({
                              id: "competition",
                            }),
                          },
                        )}
                        rules={{
                          required: formatMessage(
                            {
                              id: "form.help.required.field",
                            },
                            {
                              field: formatMessage({
                                id: "name.local",
                              }),
                            },
                          ),
                          minLength: {
                            value: 2,
                            message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                          },
                          validate: {
                            noLeadingOrTrailingSpaces: noLeadingOrTrailingSpacesReactHookFormAdapter(formatMessage),
                          },
                        }}
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                        errors={errors}
                      />
                    </Col>
                    <Col span={8}>
                      <FormControl
                        labelText="abbreviationLocal"
                        labelTooltip={formatMessage({
                          id: "form.help.local.abbreviation",
                        })}
                        name="abbreviationLocal"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row gutter={16}>
                    <Col span={16}>
                      <FormControl
                        labelText="name.latin"
                        name="nameLatin"
                        labelTooltip={formatMessage(
                          {
                            id: "form.help.latin.name.item",
                          },
                          {
                            item: formatMessage({
                              id: "conference",
                            }),
                          },
                        )}
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                    <Col span={8}>
                      <FormControl
                        labelText="abbreviationLatin"
                        labelTooltip={formatMessage({
                          id: "form.help.latin.abbreviation",
                        })}
                        name="abbreviationLatin"
                        control={control}
                        render={({ field }) => <StyledFormInputs {...field} />}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row style={{ maxWidth: 960 }} gutter={16}>
                <Col span={12}>
                  <FormControl
                    labelText="internationalReference"
                    name="internationalReference"
                    control={control}
                    labelTooltip={formatMessage({
                      id: "form.help.fixture.international.reference",
                    })}
                    render={({ field }) => <StyledFormInputs {...field} />}
                  />
                </Col>
                <Col span={12}>
                  <FormControl
                    labelText="league"
                    name="leagueId"
                    control={control}
                    labelTooltip={"form.help.competition.league"}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={[
                          { value: "", label: "" },
                          ...leagues.map((league: ILeague) => ({
                            value: league.leagueId,
                            label: league.nameLocal,
                          })),
                        ]}
                      />
                    )}
                  />
                </Col>
              </Row>
              <Row style={{ maxWidth: 960 }} gutter={16}>
                <Col span={12}>
                  <FormControl
                    labelText="ageGroup"
                    name="ageGroup"
                    control={control}
                    labelTooltip={"form.help.competition.ageGroup"}
                    render={({ field }) => (
                      <StyledFormSelect
                        style={{ width: "100%" }}
                        {...field}
                        options={[
                          { value: null, label: "" },
                          ...AGE_GROUP.map((ageGroup) => ({
                            value: ageGroup,
                            label: ageGroup,
                          })),
                        ]}
                      />
                    )}
                  />
                </Col>
                <Col span={12}>
                  <FormControl
                    labelText="externalId"
                    name="externalId"
                    control={control}
                    labelTooltip={formatMessage({ id: "form.help.competition.externalId" })}
                    render={({ field }) => <StyledFormInputs {...field} />}
                  />
                </Col>
              </Row>
              <Row>
                <Col
                  span={24}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                  }}
                >
                  <Col span={6}>
                    <Space>
                      {formatMessage({
                        id: "created_at",
                      })}
                      <i className="fas fa-xs fa-lock" />
                    </Space>
                    <StyledFormInputs style={{ color: "black" }} disabled value={dayjs(competition?.added).format("DD/MM/YYYY")} />
                  </Col>
                  <Col span={6}>
                    <Space>
                      {formatMessage({
                        id: "updated_at",
                      })}
                      <i className="fas fa-xs fa-lock" />
                    </Space>
                    <StyledFormInputs style={{ color: "black" }} disabled value={dayjs(competition?.updated).format("DD/MM/YYYY")} />
                  </Col>
                </Col>
              </Row>
              <Row style={{ marginTop: 20 }}>
                <PermissionCheck
                  permissionKeys={[PermissionName]}
                  permissionOptions={competitionId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
                >
                  <Button htmlType="submit" type="primary">
                    {competitionId ? formatMessage({ id: "competition.update" }) : formatMessage({ id: "competition.add" })}
                  </Button>
                </PermissionCheck>
                <Link to={`/${customerId}/admin/${sport}/${organizationId}/competitions`}>
                  <Button style={{ marginLeft: 30 }} type="primary">
                    {formatMessage({ id: "close" })}
                  </Button>
                </Link>
              </Row>
            </form>
          </div>
          {competition?.competitionId && (
            <RightColumn>
              <UploadImageForm
                imageType={ImageTypes.Logo}
                baseId={competition.competitionId}
                images={competition.images}
                baseType={ImageBaseTypes.Competition}
                permissionKey={PermissionName}
                onSuccess={onImageUploadSuccess}
              />
              <CompetitionHistoricNames data={competition} fetchData={() => {}} />
            </RightColumn>
          )}
        </Content>
      )}
    </Page>
  );
};

export default ManageCompetition;
